import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Badge, Button, Card, Empty, Field, Page, Table, TextInput } from '../components/ui.jsx';

/**
 * Invitations page — visible only to OWNERs.
 *
 * Creating an invite returns a full URL exactly once; the plain token
 * is never stored on our side and can't be shown again. The list view
 * therefore shows metadata only.
 */

interface Invitation {
  id: string;
  emailHint: string | null;
  note: string | null;
  expiresAt: number;
  usedAt: number | null;
  redeemedUserId: string | null;
  createdAt: number;
}

export function InvitationsPage() {
  const qc = useQueryClient();
  const [freshUrl, setFreshUrl] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['invitations'],
    queryFn: () => api<{ invitations: Invitation[] }>('/api/v1/invitations'),
  });

  const create = useMutation({
    mutationFn: (body: { emailHint?: string; note?: string; ttlDays: number }) =>
      api<{ url: string }>('/api/v1/invitations', { method: 'POST', body }),
    onSuccess: (res) => {
      setFreshUrl(res.url);
      void qc.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) =>
      api(`/api/v1/invitations/${id}/revoke`, { method: 'POST', body: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['invitations'] }),
  });

  return (
    <Page title="Invitations">
      <Card className="mb-4">
        <p className="mb-4 text-sm text-slate-500">
          Create an invite to add another owner. Every new owner has fully
          isolated data — properties, tenants, rent, expenses. Share the
          URL over any channel (WhatsApp, email, printed QR); it works
          once.
        </p>
        <NewInviteForm onSubmit={(body) => create.mutate(body)} pending={create.isPending} />
        {freshUrl && (
          <div className="mt-4 rounded border border-emerald-300 bg-emerald-50 p-3 text-sm dark:border-emerald-800 dark:bg-emerald-950">
            <div className="mb-1 font-medium text-emerald-900 dark:text-emerald-100">
              Invite ready — copy it now
            </div>
            <div className="mb-2 text-xs text-emerald-900/80 dark:text-emerald-100/80">
              This URL is shown once. Anyone with it can sign up as a new owner.
            </div>
            <div className="flex gap-2">
              <TextInput readOnly value={freshUrl} onFocus={(e) => e.currentTarget.select()} />
              <Button
                variant="secondary"
                onClick={() => {
                  void navigator.clipboard.writeText(freshUrl);
                }}
              >
                Copy
              </Button>
            </div>
          </div>
        )}
      </Card>

      {q.isLoading ? (
        <Empty>Loading…</Empty>
      ) : !q.data?.invitations.length ? (
        <Empty>No invitations yet.</Empty>
      ) : (
        <Table>
          <thead className="text-left text-slate-500 text-xs">
            <tr>
              <th className="border-b px-2 py-1 dark:border-slate-700">Email hint</th>
              <th className="border-b px-2 py-1 dark:border-slate-700">Note</th>
              <th className="border-b px-2 py-1 dark:border-slate-700">Expires</th>
              <th className="border-b px-2 py-1 dark:border-slate-700">Status</th>
              <th className="border-b px-2 py-1 dark:border-slate-700"></th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {q.data.invitations.map((inv) => (
              <tr key={inv.id} className="border-b border-slate-100 dark:border-slate-800">
                <td className="px-2 py-1">{inv.emailHint ?? '—'}</td>
                <td className="px-2 py-1">{inv.note ?? '—'}</td>
                <td className="px-2 py-1 text-slate-500">{new Date(inv.expiresAt).toLocaleString()}</td>
                <td className="px-2 py-1"><Status inv={inv} /></td>
                <td className="px-2 py-1 text-right">
                  {!inv.usedAt && inv.expiresAt > Date.now() && (
                    <Button variant="danger" onClick={() => revoke.mutate(inv.id)}>Revoke</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
}

function Status({ inv }: { inv: Invitation }) {
  if (inv.redeemedUserId) return <Badge tone="good">Redeemed</Badge>;
  if (inv.usedAt) return <Badge>Revoked</Badge>;
  if (inv.expiresAt < Date.now()) return <Badge tone="warn">Expired</Badge>;
  return <Badge tone="good">Active</Badge>;
}

function NewInviteForm({ onSubmit, pending }: {
  onSubmit: (body: { emailHint?: string; note?: string; ttlDays: number }) => void;
  pending: boolean;
}) {
  const [emailHint, setEmailHint] = useState('');
  const [note, setNote] = useState('');
  const [ttlDays, setTtlDays] = useState('7');
  function handle(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      emailHint: emailHint || undefined,
      note: note || undefined,
      ttlDays: Number(ttlDays) || 7,
    });
  }
  return (
    <form onSubmit={handle} className="grid gap-3 md:grid-cols-4 md:items-end">
      <Field label="Email hint" hint="Optional. Reminder of who this is for.">
        <TextInput type="email" value={emailHint} onChange={(e) => setEmailHint(e.target.value)} />
      </Field>
      <Field label="Note">
        <TextInput value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Rohit — Green Villa" />
      </Field>
      <Field label="Valid for (days)">
        <TextInput type="number" min={1} max={60} value={ttlDays} onChange={(e) => setTtlDays(e.target.value)} />
      </Field>
      <div className="text-right">
        <Button type="submit" disabled={pending}>{pending ? 'Creating…' : '+ New invite'}</Button>
      </div>
    </form>
  );
}
